/**
 * AdminReportDetails screen styles - V3 Design
 */

import { StyleSheet } from 'react-native'
import { heightPercentageToDP as hp, widthPercentageToDP as wp } from 'react-native-responsive-screen';
import { isWeb, isAndroid } from '../../util/platform';

import { fonts } from '../fonts';
import AppStyles from '../../AppStyles';

const styles = StyleSheet.create({
  container: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    flex: 1,
  },
  // V3: Responsive spacing - more padding on mobile for better touch targets
  mobileSpacing: {
    paddingHorizontal: isWeb() ? 0 : wp(4),
  },
  // V3: Section card container - matches Submit Report card styling
  sectionCard: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: 20,
    marginTop: hp(2),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  // V3: AI Classification card - light gold tint to distinguish system-generated
  // Note: Android renders elevation as a wide shadow that looks like a border,
  // so we skip elevation on Android and rely only on the border
  aiCard: {
    backgroundColor: 'rgba(255, 196, 81, 0.08)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 196, 81, 0.3)',
    padding: 20,
    marginTop: hp(2),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: isAndroid() ? 0 : 2,
  },
  // V3: Card title - matches Submit Report cardTitle
  cardTitle: {
    marginBottom: 12,
  },
  // V3: Card title row with edit icon
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  // V3: Subsection heading within cards
  subsectionHeading: {
    marginTop: hp(1.5),
    marginBottom: hp(0.5),
  },
  // V3: First subsection (no top margin needed)
  subsectionHeadingFirst: {
    marginBottom: hp(0.5),
  },
  // V3: Follow Up card specific styles
  followUpCard: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: 20,
    marginTop: hp(2),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  // V3: Close button container with hint directly below
  closeButtonContainer: {
    alignItems: 'flex-start',
    marginBottom: hp(2),
  },
  closeButtonHint: {
    marginTop: 6,
  },
  // V3: Comments separator within Follow Up card
  commentsSeparator: {
    height: 1,
    backgroundColor: '#E5E5E5',
    marginVertical: hp(1.5),
  },
  // V3: Report ID footer - subtle, not in card
  reportIdContainer: {
    marginTop: hp(3),
    marginBottom: hp(2),
    paddingTop: hp(2),
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
  },
  reportIdLabel: {
    marginBottom: 4,
  },
  noComments: {
    marginTop: 5,
    textAlign: 'center',
  },
  // V3: Clean card container for comment input
  orangeContainer: {
    borderRadius: 12,
    backgroundColor: AppStyles.colorSet.inputBackground,
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: wp(3.2),
    marginTop: hp(1),
    maxHeight: hp(40),
    minHeight: hp(12),
    borderColor: AppStyles.colorSet.cardBorder,
    borderWidth: 1,
  },
  // V3: Clean card container for UUID display
  orangeContainer2: {
    borderRadius: 12,
    backgroundColor: AppStyles.colorSet.inputBackground,
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: wp(3.2),
    marginTop: hp(1),
    maxHeight: hp(40),
    borderColor: AppStyles.colorSet.cardBorder,
    borderWidth: 1,
  },
  textInput: {
    width: '100%',
    fontFamily: fonts.POPPINS.regular,
    fontSize: 16,
    color: AppStyles.colorSet.textPrimary,
    flex: 1,
  },
  // V3: Gold submit button
  button: {
    cursor: 'pointer',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    height: hp(5.5),
    width: '100%',
    alignSelf: 'center',
    marginTop: hp(1.5),
  },
  // V3: Reopen button with success color
  reopenButton: {
    cursor: 'pointer',
    backgroundColor: AppStyles.colorSet.success,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    minHeight: 40,
    paddingVertical: 10,
    paddingHorizontal: 20,
    minWidth: 160,
  },
  // V3: Close button with error color (solid)
  resolveButton: {
    cursor: 'pointer',
    backgroundColor: AppStyles.colorSet.error,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    minHeight: 40,
    paddingVertical: 10,
    paddingHorizontal: 20,
    minWidth: 160,
  },
  // V3: Close button outline style (less aggressive)
  resolveButtonOutline: {
    cursor: 'pointer',
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: AppStyles.colorSet.textSecondary,
    minHeight: 40,
    paddingVertical: 10,
    paddingHorizontal: 20,
    minWidth: 160,
  },
  // V3: Override button - now positioned inline with AI card title
  overrideButton: {
    cursor: 'pointer',
    backgroundColor: AppStyles.colorSet.error,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 16,
    height: 28,
    marginLeft: 12,
    paddingHorizontal: 12,
  },
  // Case Management card - unified ticketing features
  caseManagementCard: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: 20,
    marginTop: hp(2),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  summaryStats: {
    flexDirection: 'row',
    gap: 8,
    marginLeft: 'auto',
  },
  statBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 6,
  },
  divider: {
    height: 1,
    backgroundColor: AppStyles.colorSet.cardBorder,
    marginVertical: 16,
  },
  ticketingGrid: {
    gap: 16,
  },
  editModeBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 196, 81, 0.1)',
    borderLeftWidth: 3,
    borderLeftColor: AppStyles.colorSet.brandPrimary,
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  doneButton: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginLeft: 'auto',
  },
  statusHistorySection: {
    marginTop: 8,
    marginBottom: 16,
  },
  statusHistoryToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  timeline: {
    marginTop: 12,
    paddingLeft: 12,
    borderLeftWidth: 2,
    borderLeftColor: AppStyles.colorSet.cardBorder,
  },
  timelineEntry: {
    flexDirection: 'row',
    marginBottom: 16,
    position: 'relative',
  },
  timelineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: AppStyles.colorSet.info,
    position: 'absolute',
    left: -16,
    top: 4,
  },
  timelineContent: {
    flex: 1,
    gap: 4,
  },
  // Legacy: Section headings (kept for compatibility)
  heading: {
    marginTop: hp(2),
    marginBottom: hp(0.5),
  },
  // V3: Mobile touch target improvements (minimum 44x44)
  touchTarget: {
    minHeight: 44,
    minWidth: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // V3: Mobile-optimized grid layout
  mobileGrid: isWeb() ? {} : {
    flexDirection: 'column',
    gap: 20,
  },
  // V3: Comment input with better mobile touch area
  commentInputWrapper: {
    minHeight: isWeb() ? hp(12) : hp(15),
  },
  // V3: Mobile-friendly button with larger touch area
  mobileButton: isWeb() ? {} : {
    minHeight: 50,
    paddingVertical: 14,
  },
  // V3: Responsive two-column layout for web (wide screens)
  responsiveContainer: {
    flexDirection: isWeb() ? 'row' : 'column',
    gap: isWeb() ? 24 : 0,
    alignItems: isWeb() ? 'flex-start' : 'stretch',
  },
  leftColumn: {
    flex: isWeb() ? 2 : 1,
  },
  rightColumn: {
    flex: isWeb() ? 1 : 1,
    minWidth: isWeb() ? 350 : 0,
  },
  // V3: Sticky sidebar for case management on web
  stickyColumn: isWeb() ? {
    position: 'sticky',
    top: 20,
    alignSelf: 'flex-start',
  } : {},

  // ===== V4: Single Column Layout Styles =====

  // Tags inline section - sits below report content
  tagsInlineSection: {
    marginTop: hp(2),
    paddingTop: hp(1.5),
    borderTopWidth: 1,
    borderTopColor: AppStyles.colorSet.cardBorder,
  },

  // AI tags inline - subtle gold tint
  aiTagsInline: {
    backgroundColor: 'rgba(255, 196, 81, 0.06)',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },

  // AI chips row - horizontal layout for compact display
  aiChipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
  },

  // AI chip group (label + chips)
  aiChipGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },

  // AI chip with inline hide button
  aiChipWithHide: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 196, 81, 0.15)',
    borderRadius: 16,
    paddingVertical: 4,
    paddingLeft: 8,
    paddingRight: 6,
    gap: 4,
  },

  // AI chip when hidden/overridden
  aiChipHidden: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    opacity: 0.7,
  },

  // Small hide/restore button on AI chip
  aiChipHideButton: {
    padding: 2,
    marginLeft: 2,
  },

  // Editable tags inline
  editableTagsInline: {
    marginBottom: 8,
  },

  // Tag label row with icon
  tagLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginBottom: 8,
  },
  // Web hover/focus feedback for the Edit Tags toggle — padding is cancelled by
  // an equal negative margin so the highlight adds no layout shift
  tagLabelRowHovered: {
    backgroundColor: AppStyles.stateSet.hover,
    borderRadius: 6,
    paddingHorizontal: 6,
    marginHorizontal: -6,
    paddingVertical: 2,
    marginTop: -2,
    marginBottom: 6,
  },

  // Chip row - horizontal wrap
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },

  // Tag category grouping
  tagCategory: {
    marginBottom: 10,
  },

  // Tag category label
  tagCategoryLabel: {
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // Small override button for inline use
  overrideButtonSmall: {
    backgroundColor: AppStyles.colorSet.error,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    marginLeft: 'auto',
  },

  // Restore button small - outline style for restoring AI suggestions
  restoreButtonSmall: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: AppStyles.colorSet.textSecondary,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginLeft: 'auto',
  },

  // AI tags when overridden - faded appearance
  aiTagsOverridden: {
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: AppStyles.colorSet.cardBorder,
  },

  // Overridden badge - small indicator showing hidden state
  overriddenBadge: {
    backgroundColor: AppStyles.colorSet.cardBorder,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 4,
  },

  // Compact status bar
  statusBar: {
    marginTop: hp(2),
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    overflow: 'visible',
  },

  // Admin only indicator - subtle label for admin-only sections
  adminOnlyIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
    opacity: 0.7,
  },

  // Status bar row - horizontal layout (zIndex above statusHistoryCompact for dropdown overlap)
  // Desktop: horizontal row that wraps if space runs out (never overlaps).
  statusBarRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
    zIndex: 2,
  },
  // Fixed widths on desktop so the pickers lay out predictably — `flex: 1` +
  // the picker's own `width: 100%` was the ambiguity that caused overflow.
  statusItemDesktop: {
    width: 170,
  },
  assignItemDesktop: {
    width: 260,
  },
  hugStart: {
    alignSelf: 'flex-start',
  },

  // Status bar item
  statusBarItem: {
    minWidth: isWeb() ? 140 : 'auto',
  },
  // Mobile web (narrow viewport): stack items full-width like native so the
  // dropdowns/buttons don't overflow the card. Overrides the desktop-web row.
  statusBarRowNarrow: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
  },
  statusBarItemNarrow: {
    minWidth: 0,
    // Cap width so the stacked dropdowns aren't card-wide on desktop, but stay
    // full-width on a phone (card is narrower than the cap there).
    maxWidth: 360,
    // In a column, `flex: 0` resolves to flex-basis 0% and (with the default
    // min-height 0) collapses the wrapper to zero height — the picker box then
    // overflows onto the item below. Size to content instead.
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
  },

  // Status bar item that grows
  statusBarItemGrow: {
    flex: isWeb() ? 1 : 0,
    maxWidth: isWeb() ? 280 : 'auto',
  },

  // Status bar metadata
  statusBarMeta: {
    marginLeft: isWeb() ? 'auto' : 0,
    marginTop: isWeb() ? 0 : 4,
  },

  // Escalate button
  escalateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FECACA',
    backgroundColor: '#FEF2F2',
  },
  // Web hover/focus feedback for the escalate button (deeper red)
  escalateButtonHovered: {
    borderColor: '#FCA5A5',
    backgroundColor: '#FEE2E2',
  },
  muteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#F9FAFB',
  },
  // Web hover/focus feedback for the mute button (slightly darker gray)
  muteButtonHovered: {
    borderColor: '#D1D5DB',
    backgroundColor: '#F3F4F6',
  },

  // Compact dropdown style
  compactDropdown: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderColor: AppStyles.colorSet.inputBorder,
    borderRadius: 8,
    minHeight: 40,
  },

  // Compact dropdown disabled state
  compactDropdownDisabled: {
    opacity: 0.5,
    backgroundColor: AppStyles.colorSet.cardBorder,
  },

  // Compact dropdown container
  compactDropdownContainer: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderColor: AppStyles.colorSet.inputBorder,
    borderRadius: 8,
  },

  // Compact dropdown text
  compactDropdownText: {
    fontFamily: 'Poppins-Regular',
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
  },

  // Status history compact (zIndex below statusBarRow so dropdowns render on top)
  statusHistoryCompact: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: AppStyles.colorSet.cardBorder,
    zIndex: 1,
  },

  // Single column section
  singleColumnSection: {
    marginTop: hp(2),
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: 16,
  },

  // Comments section at bottom
  commentsSection: {
    marginTop: hp(2),
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: 16,
  },
});

export default styles;
